import React from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import RuleForm from "./RuleForm";
import {
  createShoppingCartRule,
  getShoppingCartRules,
  updateShoppingCartRule,
} from "app/services/api/shoppingCart";
import { Link } from "react-router-dom";
import Div from "@jumbo/shared/Div";

const EditRule = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const { ruleId } = useParams();
  const isNew = !ruleId;
  const isAutoMode = location.pathname.includes("/auto");

  const [loading, setLoading] = React.useState(false);
  const [initialValues, setInitialValues] = React.useState(null);

  const hideCodesTab = React.useMemo(() => {
    return isAutoMode || Boolean((initialValues || {}).autoApply);
  }, [isAutoMode, initialValues]);

  React.useEffect(() => {
    let isMounted = true;
    const load = async () => {
      if (!ruleId) return; // new rule
      try {
        setLoading(true);
        const rules = await getShoppingCartRules();
        const found = (Array.isArray(rules) ? rules : []).find(
          (r) => String(r._id) === String(ruleId)
        );
        if (isMounted) setInitialValues(found || null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [ruleId]);

  const handleSave = async (rule) => {
    setLoading(true);
    try {
      await (isNew ? createShoppingCartRule : updateShoppingCartRule)(rule);
      enqueueSnackbar(
        t("notifications.savedSuccessfully", {
          defaultValue: "Saved successfully",
        }),
        { variant: "success" }
      );
    } catch (error) {
      enqueueSnackbar(
        t("notifications.savedFailed", {
          defaultValue: "Saved failed",
        }),
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }

    navigate(-1);
  };

  return (
    <React.Fragment>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link to="/">{t("sidebar.menuItem.home")}</Link>
        <Link
          to={
            isAutoMode
              ? "/customer/dsok/shopping-cart-auto-discounts"
              : "/customer/dsok/shopping-cart-rules"
          }
        >
          {t("pages.dsokCartRules.title")}
        </Link>
        <Typography color="text.primary">
          {isNew
            ? t("pages.dsokCartRules.newRule", { defaultValue: "New Rule" })
            : initialValues?.name ||
              t("pages.dsokCartRules.editRule", { defaultValue: "Edit Rule" })}
        </Typography>
      </Breadcrumbs>
      <Div
        sx={{
          display: "flex",
          width: "100%",
          p: 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            backgroundColor: "white",
            borderRadius: 1,
            boxShadow: 1,
            p: 2,
          }}
        >
          {loading && !isNew ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack>
              <RuleForm
                initialValues={initialValues || undefined}
                onSave={handleSave}
                onCancel={() => navigate(-1)}
                isAutoMode={isAutoMode}
                hideCodesTab={hideCodesTab}
                isNew={isNew}
              />
            </Stack>
          )}
        </Box>
      </Div>
    </React.Fragment>
  );
};

export default EditRule;
